import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getStage, type Stage, type StageVoice } from "./stages";
import { useMatch, type MatchView } from "./useMatch";
import type { Whisper } from "./campaign/act1.ts";
import { dName, dTitle, loc } from "./campaign/loc.ts";
import { gt, useGt } from "./campaign/gameText.ts";
import { useLang, t } from "../gamzone/i18n.ts";
import { SIZE, type Outcome } from "./othello";
import { SKIP_EVENT, LOSE_EVENT, DRAW_EVENT } from "../app/gameConfig.ts";
import { addScore, addPoints, spendPoints, usePoints } from "../gamzone/points.ts";
import { getReducedMotion } from "../gamzone/settings.ts";
import { AdSlot } from "../gamzone/ads/AdSlot.tsx";
import { RewardedAd } from "../gamzone/ads/RewardedAd.tsx";
import { Interstitial } from "../gamzone/ads/Interstitial.tsx";
import { BannerInterstitial } from "../gamzone/ads/BannerInterstitial.tsx";
import { getInterstitialBanner, isWebRuntime, type AdminBanner } from "../gamzone/ads/banner.ts";
import { ADMOB_IDLE_INTERSTITIAL_UNIT_ID } from "../gamzone/ads/config.ts";
import { playSfx } from "./audio/sfx.ts";

const RETRY_COST = 10;
const REWARD_AD_POINTS = 300;

// Legal-move markers are an early-game aid: shown through stage 3, off after.
const HINT_MAX_STAGE = 3;
// Turn-bar reactions.
const SLOW_MS = 13000; // player idle this long on their turn → the "slow" line
const BEHIND_BY = 8; // player trailing by this many discs → the "behind" line
// Flip animation stagger — each flipped disc starts this much later than the
// previous one (in view.lastFlipped's order) instead of all flipping at once.
// Large enough relative to bo-flip's duration (game.css) that discs read as
// one-after-another instead of nearly-simultaneous with a slight offset.
const FLIP_STAGGER_MS = 130;
// 일시정지/패배/무승부/승리 팝업에서 이만큼 조작 없이 있으면 전면광고 한 번 노출.
const IDLE_AD_MS = 60_000;

/** Default win phrase when a stage doesn't set its own (stages 1–2). */
function defaultWinPhrase(opponentName: string, lang: "ko" | "en"): string {
  const name = dName(opponentName, lang);
  return lang === "en" ? `Defeated ${name}` : `${name}을 이겼습니다`;
}

/**
 * The 8×8 duel for a given stage. Reached only after that stage's campaign
 * dialogue, so there is no in-match intro overlay; play starts immediately. On
 * a win the result overlay's button runs `onWin` (→ the stage outro); a
 * loss/draw restarts. The info-panel CTA restarts the match.
 */
export function MatchScreen({
  stageId,
  onWin,
  onExitToMap,
}: {
  stageId: number;
  onWin: () => void;
  onExitToMap: () => void;
}) {
  const lang = useLang();
  const stage = useMemo(() => getStage(stageId), [stageId]);
  const opp = stage.opponentName;
  // The disc-playing foe's label for result lines — 세라's stage overrides it
  // with "짐승" since the AI is the 균열 짐승, not 세라.
  const foe = stage.foeName ?? opp;
  const hints = stage.id <= HINT_MAX_STAGE;
  const [paused, setPaused] = useState(false);
  const [rewardText, setRewardText] = useState<string | null>(null);
  // "광고보기" on the 패배/무승부 popup — RewardedAd.tsx grants REWARD_AD_POINTS
  // only if the player actually earns the reward (see gamzone/ads/RewardedAd.tsx).
  const [watchingRewardAd, setWatchingRewardAd] = useState(false);
  const points = usePoints();
  // Keyboard cursor on the board (index 0..63). PC parity with the other
  // Gamzone games: ← ↑ → ↓ move it, Enter/Space places on it.
  const [cursor, setCursor] = useState(27);
  // True once the player has sat on their turn for SLOW_MS — drives the
  // opponent's mild-impatience line. Resets every time the turn changes.
  const [slow, setSlow] = useState(false);

  const handleEnd = useCallback(
    (outcome: Outcome, playerCount: number) => {
      const won = outcome === stage.playerDisc;
      // No point reward for a win or a loss — only the disc-count record.
      const top = addScore(playerCount);
      if (won) {
        const phrase = stage.winPhrase ? loc(stage.winPhrase, lang) : defaultWinPhrase(opp, lang);
        const bestLabel = lang === "en" ? "Best" : "최고";
        const unitLabel = lang === "en" ? "" : "칸";
        setRewardText(`${phrase} · ${bestLabel} ${top[0] ?? playerCount}${unitLabel}`);
      } else if (outcome === "draw") {
        setRewardText(gt("draw"));
      } else {
        const foeLabel = dName(foe, lang);
        setRewardText(lang === "en" ? `${foeLabel} came out ahead this time` : `이번엔 ${foeLabel}이 앞섰습니다`);
      }
    },
    [stage, opp, foe, lang],
  );

  const { view, play, restart, finish } = useMatch(stage, {
    reducedMotion: getReducedMotion(),
    onEnd: handleEnd,
    paused,
  });

  const onRestart = useCallback(() => {
    setRewardText(null);
    setPaused(false);
    setCursor(27);
    restart();
  }, [restart]);

  // 돌 놓기/뒤집기 SFX — fires on every committed move (player or AI, both go
  // through useMatch's commitMove and bump moveNo). Guarded to strictly
  // increasing moveNo so restart()'s reset back to 1 doesn't replay it.
  const prevMoveNoRef = useRef(view.moveNo);
  useEffect(() => {
    if (view.moveNo > prevMoveNoRef.current) {
      playSfx("place");
      if (view.lastFlipped.length > 0) {
        playSfx("flip", { flipCount: view.lastFlipped.length });
      }
    }
    prevMoveNoRef.current = view.moveNo;
  }, [view.moveNo, view.lastFlipped]);

  // 일시정지/패배/무승부/승리 팝업을 IDLE_AD_MS 동안 그대로 두면(조작 없음) 전면광고
  // 한 번 띄움 — 팝업이 바뀌거나(다른 종류로) 닫히면 타이머 리셋, 한 팝업당 1회만.
  // 보상형 광고를 보는 중엔(그 자체가 조작이므로) 재우지 않음.
  const overlayShowing = (paused || view.ended) && !watchingRewardAd;
  const [idleAdVisible, setIdleAdVisible] = useState(false);
  useEffect(() => {
    if (!overlayShowing) {
      setIdleAdVisible(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setIdleAdVisible(true), IDLE_AD_MS);
    return () => window.clearTimeout(timer);
  }, [overlayShowing]);

  // Web (any device) has no AdMob, so the idle nudge falls back to the
  // admin-managed "전면배너" there (see gamzone/ads/banner.ts) instead of the
  // AdMob unit below — only the native app keeps that path. Fetched as soon
  // as the popup shows (not when the timer fires) so it's already resolved
  // by IDLE_AD_MS and doesn't flash the AdMob placeholder first.
  const [idleBanner, setIdleBanner] = useState<AdminBanner | null>(null);
  useEffect(() => {
    if (!overlayShowing || !isWebRuntime()) {
      setIdleBanner(null);
      return undefined;
    }
    let cancelled = false;
    getInterstitialBanner().then((banner) => { if (!cancelled) setIdleBanner(banner); });
    return () => { cancelled = true; };
  }, [overlayShowing]);

  // DEV-only: the control-legend d-pad glyphs are always visible (App.tsx),
  // but only wired to fire these events with `?dev` in the URL. Force a match
  // outcome so each result screen is easy to check — ▶ win, ◀ loss, ▼ draw.
  // All three land on that outcome's result popup (win → continue runs onWin →
  // outro; loss/draw → play again).
  useEffect(() => {
    const win = () => finish(view.playerDisc);
    const lose = () => finish(view.aiDisc);
    const draw = () => finish("draw");
    window.addEventListener(SKIP_EVENT, win);
    window.addEventListener(LOSE_EVENT, lose);
    window.addEventListener(DRAW_EVENT, draw);
    return () => {
      window.removeEventListener(SKIP_EVENT, win);
      window.removeEventListener(LOSE_EVENT, lose);
      window.removeEventListener(DRAW_EVENT, draw);
    };
  }, [finish, view.playerDisc, view.aiDisc]);

  // Arm the "slow" line while it's the player's turn; clear it on any turn/move
  // change (moveNo bumps on every placement) or when paused/ended.
  const myTurn = !view.ended && view.turn === view.playerDisc;
  useEffect(() => {
    setSlow(false);
    if (!myTurn || paused) return undefined;
    const t = window.setTimeout(() => setSlow(true), SLOW_MS);
    return () => window.clearTimeout(t);
  }, [myTurn, paused, view.moveNo]);

  // 세라's stage only: the companion (세라·린·브람) voices the player-turn side
  // of the turn bar. 세라's lines are state-picked; 린·브람 each fire once — on
  // the first eligible player turn — then it's back to 세라.
  const oneShotRef = useRef<{ rin: number | null; bram: number | null }>({
    rin: null,
    bram: null,
  });

  // The "behind" turn-bar line is a ONE-SHOT across every stage: it shows only
  // on the first player turn the disc gap first reaches ≥8, then never again
  // (latched to that moveNo, like the 린/브람 companion one-shots). Applies to
  // `voice.behind` (normal), `c.behind` (companion) and `behindAlly` alike.
  const behindShotRef = useRef<number | null>(null);
  const onMyTurn = !view.ended && view.turn === view.playerDisc;
  if (
    behindShotRef.current === null &&
    onMyTurn &&
    view.aiCount - view.playerCount >= BEHIND_BY
  ) {
    behindShotRef.current = view.moveNo;
  }
  const behindOnce = onMyTurn && behindShotRef.current === view.moveNo;

  // An ally takes the turn bar on a normal (non-companion) stage, with a hero
  // art swap for that stretch:
  //  · `cheer` — one-shot, on the player's turn at `cheer.atMove`+ (e.g. 린 on 토르)
  //  · `behindAlly` — the one-shot "behind" line, voiced by an ally (e.g. 토르 on 칼데르)
  // Both feed the same `cheerLine` (turn-bar text) + `cheerPortrait` (hero art).
  const cheerRef = useRef<number | null>(null);
  let cheerLine: Whisper | null = null;
  let cheerPortrait: string | null = null;
  if (stage.cheer && onMyTurn) {
    if (cheerRef.current == null && view.moveNo >= stage.cheer.atMove) {
      cheerRef.current = view.moveNo;
    }
    if (cheerRef.current === view.moveNo) {
      cheerLine = { name: stage.cheer.name, text: stage.cheer.text };
      cheerPortrait = stage.cheer.portrait;
    }
  }
  if (!cheerLine && stage.behindAlly && !stage.companion && behindOnce) {
    cheerLine = { name: stage.behindAlly.name, text: stage.behindAlly.text };
    cheerPortrait = stage.behindAlly.portrait;
  }

  const turnLine =
    stage.companion && !view.ended && view.turn === view.playerDisc
      ? pickCompanionLine(stage, view, slow, behindOnce, oneShotRef.current)
      : cheerLine;

  // The hero illustration follows whoever last spoke and holds through the
  // 짐승's turn, so it swaps only on an actual speaker change.
  const speakerRef = useRef(stage.opponentName);
  if (turnLine && stage.companion) speakerRef.current = turnLine.name;
  const heroSrc = cheerPortrait
    ? cheerPortrait
    : (stage.companion?.portraits[speakerRef.current] ?? stage.art.opponent);

  // Keyboard control — PC parity with the other games: arrows move the cursor,
  // Enter/Space commits the legal move nearest to it. Dialogs stop key events
  // at capture phase, so this window listener stays quiet while one is open.
  const legalRef = useRef<number[]>([]);
  legalRef.current = view.legal;
  const canPlayRef = useRef(false);
  canPlayRef.current = view.canPlay;
  useEffect(() => {
    if (view.ended || paused) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const r = Math.floor(cursor / SIZE);
      const c = cursor % SIZE;
      switch (e.key) {
        case "ArrowUp": e.preventDefault(); setCursor((Math.max(0, r - 1)) * SIZE + c); return;
        case "ArrowDown": e.preventDefault(); setCursor((Math.min(SIZE - 1, r + 1)) * SIZE + c); return;
        case "ArrowLeft": e.preventDefault(); setCursor(r * SIZE + Math.max(0, c - 1)); return;
        case "ArrowRight": e.preventDefault(); setCursor(r * SIZE + Math.min(SIZE - 1, c + 1)); return;
        case "Enter":
        case " ": {
          if (!canPlayRef.current || legalRef.current.length === 0) return;
          e.preventDefault();
          // nearest legal cell to the cursor (Manhattan distance)
          let best = legalRef.current[0];
          let bestD = Infinity;
          for (const m of legalRef.current) {
            const d = Math.abs(Math.floor(m / SIZE) - r) + Math.abs((m % SIZE) - c);
            if (d < bestD) { bestD = d; best = m; }
          }
          setCursor(best);
          play(best);
          return;
        }
        default:
          return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cursor, view.ended, paused, play]);

  const actLabel = loc(stage.act, lang).replace(/^[IVXLC]+\.\s*/, "");
  const meLabel = gt("me");
  const foeLabel = dName(foe, lang);

  return (
    <div className="bo-screen">
      <section
        className="bo-card"
        style={{ ["--bo-bg" as string]: `url(${stage.art.background})` }}
      >
        <div className="bo-hero">
          <img
            key={heroSrc}
            className="bo-opp-art"
            src={heroSrc}
            alt=""
            aria-hidden
          />
          <div className="bo-topbar">
            <div className="bo-topbar-slot left" />
            <div className="bo-stage">
              <span className="bo-stage-no">STAGE {stage.id}</span>
              <span className="bo-stage-title">{actLabel}</span>
              {stage.opponentTitle && (
                <span className="bo-stage-opp">{dTitle(stage.opponentTitle, lang)}</span>
              )}
            </div>
            <div className="bo-topbar-slot right" />
          </div>

          {/* Pinned to the hero's own bottom edge (not in normal flow) so a
             wrapped 2-3 line message grows upward over the portrait instead
             of pushing the HUD/board down — see .bo-turnbar's `position`. */}
          <TurnBar
            view={view}
            oppName={foeLabel}
            voice={stage.voice}
            slow={slow}
            behindOnce={behindOnce}
            companionLine={turnLine}
          />
        </div>

        {!view.ended && !paused && (
          <button
            type="button"
            className="bo-pause-btn"
            aria-label={gt("pause")}
            onClick={() => setPaused(true)}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
              stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <path d="M8 5v14M16 5v14" />
            </svg>
          </button>
        )}

        <Hud view={view} stage={stage} />

        {/* [수정 2026-09-10 · 커서/마지막수 세션] mouse click moves the cursor
           too (keyboard Enter already did) so the sky-blue mark lands on your
           last move instead of the start square. 병행 작업 시 이 onPlay 래퍼 유지. */}
        <Board
          view={view}
          hints={hints}
          cursor={cursor}
          villain={stage.role === "villain"}
          onPlay={(cell) => {
            setCursor(cell);
            play(cell);
          }}
        />

        <footer className="bo-controls">
          <AdSlot />
        </footer>

        {paused && !view.ended && (
          <Overlay>
            <h2 className="bo-result-title">{gt("pause")}</h2>
            <div className="bo-result-actions">
              <button
                className="bo-btn bo-btn-accent bo-btn-go"
                onClick={() => setPaused(false)}
              >
                {t("resume")}
              </button>
              <div className="bo-pause-actions-row">
                <button
                  className="bo-btn bo-btn-retry"
                  disabled={points < RETRY_COST}
                  onClick={() => {
                    if (spendPoints(RETRY_COST)) onRestart();
                  }}
                >
                  {t("restart")} · {RETRY_COST}P
                </button>
                <button className="bo-btn" onClick={onExitToMap}>
                  {gt("toMap")}
                </button>
              </div>
            </div>
          </Overlay>
        )}

        {view.ended && view.outcome && (
          <Overlay>
            {view.outcome === stage.playerDisc ? (
              <>
                <h2 className="bo-result-title">{stage.winTitle ? loc(stage.winTitle, lang) : gt("victory")}</h2>
                <p className="bo-result-score">
                  {meLabel} {view.playerCount} : {view.aiCount} {foeLabel}
                </p>
                {rewardText && <p className="bo-reward">{rewardText}</p>}
                <div className="bo-result-actions">
                  <button
                    className="bo-btn bo-btn-accent bo-btn-go"
                    onClick={onWin}
                  >
                    {gt("continueBtn")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="bo-result-title">
                  {view.outcome === "draw" ? gt("draw") : gt("defeat")}
                </h2>
                <p className="bo-result-score">
                  {meLabel} {view.playerCount} : {view.aiCount} {foeLabel}
                </p>
                <p className="bo-result-line">{loc(stage.loseLine, lang)}</p>
                {rewardText && <p className="bo-reward">{rewardText}</p>}
                <div className="bo-result-actions">
                  <button
                    className="bo-btn bo-btn-accent bo-btn-retry"
                    disabled={points < RETRY_COST}
                    onClick={() => {
                      if (spendPoints(RETRY_COST)) onRestart();
                    }}
                  >
                    {t("playAgain")} · {RETRY_COST}P
                  </button>
                  <button
                    className="bo-btn"
                    onClick={() => setWatchingRewardAd(true)}
                  >
                    {gt("watchAd")}
                  </button>
                  <button className="bo-btn" onClick={onExitToMap}>
                    {gt("toMap")}
                  </button>
                </div>
                <p className="bo-note">{gt("adRewardNote", { points: REWARD_AD_POINTS })}</p>
              </>
            )}
          </Overlay>
        )}

        {watchingRewardAd && (
          <RewardedAd
            continueLabel={gt("continueBtn")}
            onClose={() => setWatchingRewardAd(false)}
            onReward={() => addPoints(REWARD_AD_POINTS)}
          />
        )}

        {idleAdVisible && idleBanner && (
          <BannerInterstitial banner={idleBanner} onClose={() => setIdleAdVisible(false)} />
        )}
        {idleAdVisible && !idleBanner && (
          <Interstitial
            real
            adUnitId={ADMOB_IDLE_INTERSTITIAL_UNIT_ID}
            continueLabel={gt("continueBtn")}
            onContinue={() => setIdleAdVisible(false)}
          />
        )}
      </section>
    </div>
  );
}

/** 세라's stage: which companion line the turn bar shows on the player's turn.
 *  Priority: behind > slow > 브람 > 린 > 첫 턴(moveNo≤1) > 기본. 세라's lines
 *  are state-driven; `behind`, 브람(moveNo≥14) and 린(moveNo≥6) each fire once —
 *  `behindOnce` (from the caller's shared latch) and `oneShot` record the moveNo
 *  they fired on, so they show on that one move and then it reverts to 세라.
 *  `stage.companion` is asserted by the caller. */
function pickCompanionLine(
  stage: Stage,
  view: MatchView,
  slow: boolean,
  behindOnce: boolean,
  oneShot: { rin: number | null; bram: number | null },
): Whisper {
  const comp = stage.companion!;
  const c = comp.turnLines;
  if (behindOnce) return c.behind;
  if (slow) return c.slow;
  if (c.bram) {
    const at = comp.bramAtMove ?? 14;
    if (oneShot.bram === null && view.moveNo >= at) oneShot.bram = view.moveNo;
    if (oneShot.bram !== null && view.moveNo === oneShot.bram) return c.bram;
  }
  if (c.rin) {
    const at = comp.rinAtMove ?? 6;
    if (oneShot.rin === null && view.moveNo >= at) oneShot.rin = view.moveNo;
    if (oneShot.rin !== null && view.moveNo === oneShot.rin) return c.rin;
  }
  if (view.moveNo <= 1 && c.firstTurn) return c.firstTurn;
  return c.turn;
}

/* -------------------------------- HUD -------------------------------- */

function Hud({ view, stage }: { view: MatchView; stage: ReturnType<typeof getStage> }) {
  const lang = useLang();
  const gt = useGt();
  return (
    <div className="bo-hud">
      <div className={`bo-side ${view.turn === view.playerDisc ? "is-turn" : ""}`}>
        <span className={`bo-disc-badge ${view.playerDisc}`} />
        <span className="bo-side-label">{gt("me")}</span>
        <span className="bo-side-score">{String(view.playerCount).padStart(2, "0")}</span>
      </div>
      <span className="bo-hud-vs">VS</span>
      <div className={`bo-side right ${view.turn === view.aiDisc ? "is-turn" : ""}`}>
        <span className="bo-side-score">{String(view.aiCount).padStart(2, "0")}</span>
        <span className="bo-side-label">{dName(stage.opponentName, lang)}</span>
        <span className={`bo-disc-badge ${view.aiDisc}`} />
      </div>
    </div>
  );
}

function TurnBar({
  view,
  oppName,
  voice,
  slow,
  behindOnce,
  companionLine,
}: {
  view: MatchView;
  oppName: string;
  voice: StageVoice;
  slow: boolean;
  behindOnce: boolean;
  companionLine: Whisper | null;
}) {
  const lang = useLang();
  const gt = useGt();
  let msg: string;
  if (view.ended) {
    msg = gt("duelEnd");
  } else if (view.turn === view.playerDisc) {
    // the one-shot "behind" nudge (first time ≥8 behind) beats mild impatience
    msg = behindOnce ? loc(voice.behind, lang) : slow ? loc(voice.slow, lang) : loc(voice.turn, lang);
  } else {
    msg = loc(voice.thinking, lang);
  }

  // On 세라's stage the companion line replaces the prompt on the player's
  // turn; the speaker is shown by the hero art, not a name here.
  const text = companionLine && !view.ended ? loc(companionLine.text, lang) : msg;

  return (
    <div className="bo-turnbar">
      <span>{text}</span>
      <span className="bo-move">
        {view.passBy && (
          <b className="bo-pass">
            {gt("turnSkipped", { who: view.passBy === view.playerDisc ? gt("me") : oppName })} ·{" "}
          </b>
        )}
        {gt("moveCounter", { n: String(view.moveNo).padStart(2, "0"), empty: view.emptyCount })}
      </span>
    </div>
  );
}

/* ------------------------------- Board ------------------------------ */

export function Board({
  view,
  hints,
  cursor,
  onPlay,
  villain,
}: {
  view: MatchView;
  hints: boolean;
  cursor: number;
  onPlay: (cell: number) => void;
  /** Villain stages (막 IV–V, 적 선공) swap in the red board-concept-v2
   *  art with a slow opacity pulse — see game.css's .bo-board.villain. */
  villain?: boolean;
}) {
  const gt = useGt();
  const legal = new Set(view.legal);
  // view.lastFlipped is already ordered outward from the placed cell within
  // each of the 8 directions (see othello.ts's flipsFor) — used as the flip
  // sequence, one disc after another, instead of every flip animating at once.
  const flipOrder = new Map(view.lastFlipped.map((cell, i) => [cell, i]));

  return (
    <div
      className={"bo-board" + (villain ? " villain" : "")}
      role="grid"
      aria-label={gt("boardAria")}
      aria-rowcount={SIZE}
      aria-colcount={SIZE}
    >
      {view.board.map((cell, i) => {
        const isLegal = view.canPlay && legal.has(i);
        const row = Math.floor(i / SIZE) + 1;
        const col = (i % SIZE) + 1;
        return (
          <button
            key={i}
            type="button"
            role="gridcell"
            aria-rowindex={row}
            aria-colindex={col}
            className={
              "bo-cell" +
              (isLegal && hints ? " legal" : "") +
              (i === cursor ? " cursor" : "") +
              (i === view.lastPlaced ? " placed" : "") +
              (flipOrder.has(i) ? " flipped" : "")
            }
            aria-label={
              cell
                ? gt("cellFilled", { row, col, color: cell === "dark" ? gt("discDark") : gt("discLight") })
                : isLegal
                  ? gt("cellPlayable", { row, col })
                  : gt("cellEmpty", { row, col })
            }
            disabled={!isLegal}
            onClick={() => onPlay(i)}
          >
            {cell && (
              <span
                // Keyed so a disc flipped in back-to-back moves remounts
                // instead of keeping its "flipped" class across renders —
                // without this, the CSS animation silently doesn't replay
                // (class never toggles off, so the browser never re-triggers
                // it) whenever the same cell flips twice in a row.
                key={flipOrder.has(i) ? `flip-${i}-${view.moveNo}` : `static-${i}`}
                className={`bo-disc ${cell}`}
                style={
                  flipOrder.has(i)
                    ? { animationDelay: `${flipOrder.get(i)! * FLIP_STAGGER_MS}ms` }
                    : undefined
                }
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

export function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div className="bo-overlay">
      <div className="bo-overlay-inner">{children}</div>
    </div>
  );
}
